import { useState, useEffect } from 'react';
import { analyticsService } from '@/services/analyticsService';
import {
   invitationsService,
   InvitationDetailResponse,
   ExpiringInvitationsResponse,
} from '@/services/invitationsService';
import { InvitationStatistics } from '@/types';
import {
   PieChart,
   Pie,
   Cell,
   ResponsiveContainer,
   BarChart,
   Bar,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { Eye, Calendar, User, Mail } from 'lucide-react';
import { Clock, AlertTriangle } from 'lucide-react';
import InvitationDetailModal from '@/components/InvitationDetailModal';
import CategoryStatsModal from '@/components/CategoryStatsModal';

// Mock recent invitations data (this would normally come from Firebase)
const mockRecentInvitations = [
   {
      id: '5GIdZZc1FcbcJpidzfN0',
      type: 'album' as const,
      status: 'pending',
      inviterName: 'Austin',
      recipient_email: 'nwaforaugustine922@gmail.com',
      createdAt: '2025-07-23T15:48:08Z',
      role: 'editor',
   },
   {
      id: '10hcFwzfYnE5cWTT06NM',
      type: 'event' as const,
      status: 'pending',
      inviterName: 'Manny Jones',
      recipient_email: 'k.z.mcclendon@gmail.com',
      createdAt: '2025-06-21T16:09:37Z',
      role: 'editor',
      eventTitle: 'Porch Party in the Heart of Philadelphia',
   },
   {
      id: '2U0JhEwvEKFVP8K7JkNw',
      type: 'trip' as const,
      status: 'accepted',
      inviterName: 'Emmanuel Jones',
      recipient_email: 'pe@xisglobal.co',
      acceptedAt: '2025-07-08T20:27:48Z',
      createdAt: '2025-07-08T18:53:05Z',
      role: 'editor',
      customMessage: 'Please join trip',
   },
   {
      id: 'abc123def456',
      type: 'album' as const,
      status: 'accepted',
      inviterName: 'Sarah Wilson',
      recipient_email: 'sarah.w@example.com',
      createdAt: '2025-07-15T09:30:00Z',
      acceptedAt: '2025-07-16T14:22:00Z',
      role: 'viewer',
   },
   {
      id: 'xyz789uvw012',
      type: 'event' as const,
      status: 'rejected',
      inviterName: 'Mike Rodriguez',
      recipient_email: 'mike.r@company.com',
      createdAt: '2025-07-10T11:15:00Z',
      role: 'editor',
      eventTitle: 'Team Building Workshop',
   },
   {
      id: 'qwe345rty678',
      type: 'trip' as const,
      status: 'pending',
      inviterName: 'Lisa Chen',
      recipient_email: 'lisa.chen@travel.com',
      createdAt: '2025-07-20T16:45:00Z',
      role: 'editor',
      customMessage: 'Would love to have you join our adventure!',
   },
];

// Verify mock data structure
console.log('🔍 Mock data verification:');
console.log('Total invitations:', mockRecentInvitations.length);
console.log('Albums:', mockRecentInvitations.filter((inv) => inv.type === 'album').length);
console.log('Events:', mockRecentInvitations.filter((inv) => inv.type === 'event').length);
console.log('Trips:', mockRecentInvitations.filter((inv) => inv.type === 'trip').length);
console.log('Accepted:', mockRecentInvitations.filter((inv) => inv.status === 'accepted').length);
console.log('Pending:', mockRecentInvitations.filter((inv) => inv.status === 'pending').length);
console.log('Rejected:', mockRecentInvitations.filter((inv) => inv.status === 'rejected').length);

// Helper function to generate realistic invitation data based on statistics
const generateInvitationsForCategory = (
   type: 'album' | 'event' | 'trip',
   stats: { accepted: number; pending: number; rejected: number; invited: number; total: number },
   filterStatus?: 'accepted' | 'pending' | 'rejected' | 'invited' | 'total'
) => {
   const invitations: any[] = [];
   const sampleNames = [
      'John Smith',
      'Sarah Wilson',
      'Mike Johnson',
      'Lisa Chen',
      'David Brown',
      'Emma Davis',
      'James Miller',
      'Anna Garcia',
      'Robert Taylor',
      'Maria Rodriguez',
      'Austin Anderson',
      'Manny Jones',
      'Emmanuel Jones',
      'Lisa Chen',
      'Patricia Wilson',
   ];
   const sampleEmails = [
      'john.smith@example.com',
      'sarah.w@company.com',
      'mike.j@email.com',
      'lisa.chen@travel.com',
      'david.b@work.com',
      'emma.davis@mail.com',
      'james.m@example.org',
      'anna.g@company.net',
      'robert.t@email.co',
      'maria.r@example.com',
      'nwaforaugustine922@gmail.com',
      'k.z.mcclendon@gmail.com',
      'pe@xisglobal.co',
      'patricia@example.com',
      'admin@company.com',
   ];

   const statusArray: ('accepted' | 'pending' | 'rejected' | 'invited')[] = [];

   // Build status array based on actual counts
   for (let i = 0; i < stats.accepted; i++) statusArray.push('accepted');
   for (let i = 0; i < stats.pending; i++) statusArray.push('pending');
   for (let i = 0; i < stats.rejected; i++) statusArray.push('rejected');
   for (let i = 0; i < stats.invited; i++) statusArray.push('invited');

   // Generate invitations for each status
   statusArray.forEach((status, index) => {
      // Filter by status if specified
      if (filterStatus && filterStatus !== 'total' && status !== filterStatus) {
         return;
      }

      const nameIndex = index % sampleNames.length;
      const emailIndex = index % sampleEmails.length;
      const baseDate = new Date();
      baseDate.setDate(baseDate.getDate() - Math.floor(Math.random() * 30)); // Random date in last 30 days

      const invitation: any = {
         id: `${type}_${status}_${index}`,
         type,
         status,
         inviterName: sampleNames[nameIndex],
         recipient_email: sampleEmails[emailIndex],
         createdAt: baseDate.toISOString(),
         role: Math.random() > 0.5 ? 'editor' : 'viewer',
      };

      // Add expiration date (typically 7-30 days from creation)
      const expirationDate = new Date(baseDate);
      expirationDate.setDate(expirationDate.getDate() + Math.floor(Math.random() * 23) + 7); // 7-30 days
      invitation.expiresAt = expirationDate.toISOString();

      // Calculate days until expiry for display
      const now = new Date();
      invitation.daysUntilExpiry = Math.ceil(
         (expirationDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
      );

      // Add type-specific fields
      if (type === 'event') {
         invitation.eventTitle = `Sample ${type.charAt(0).toUpperCase() + type.slice(1)} Event ${
            index + 1
         }`;
         invitation.location =
            index % 3 === 0 ? 'New York' : index % 3 === 1 ? 'Los Angeles' : 'Chicago';
      } else if (type === 'trip') {
         invitation.customMessage =
            index % 2 === 0 ? 'Please join our adventure!' : 'Would love to have you with us!';
      }

      // Add acceptance date if accepted
      if (status === 'accepted') {
         const acceptedDate = new Date(baseDate);
         acceptedDate.setHours(acceptedDate.getHours() + Math.floor(Math.random() * 48));
         invitation.acceptedAt = acceptedDate.toISOString();
      }

      invitations.push(invitation);
   });

   return invitations;
};

export const InvitationsPage = () => {
   const [data, setData] = useState<InvitationStatistics | null>(null);
   const [loading, setLoading] = useState(true);
   const [startDate, setStartDate] = useState(() => {
      const date = new Date();
      date.setDate(date.getDate() - 30); // Default to 30 days ago
      return date.toISOString().split('T')[0];
   });
   const [endDate, setEndDate] = useState(() => {
      return new Date().toISOString().split('T')[0]; // Default to today
   });

   // Modal state
   const [selectedInvitation, setSelectedInvitation] = useState<{
      id: string;
      type: 'album' | 'event' | 'trip';
   } | null>(null);
   const [invitationDetail, setInvitationDetail] = useState<InvitationDetailResponse | null>(null);
   const [modalLoading, setModalLoading] = useState(false);
   const [modalOpen, setModalOpen] = useState(false);

   // Statistics detail modal state
   const [statsModalOpen, setStatsModalOpen] = useState(false);
   const [selectedCategory, setSelectedCategory] = useState<
      'albums' | 'events' | 'trips' | 'total' | null
   >(null);
   const [selectedStatus, setSelectedStatus] = useState<
      'accepted' | 'pending' | 'rejected' | 'invited' | 'total' | null
   >(null);
   const [categoryInvitations, setCategoryInvitations] = useState<any[]>([]);
   const [statsModalLoading, setStatsModalLoading] = useState(false);

   // Expiring invitations state - removed since we don't need filtering anymore

   // Pagination state for expiring and expired invitations
   const [expiringCurrentPage, setExpiringCurrentPage] = useState(1);
   const [expiredCurrentPage, setExpiredCurrentPage] = useState(1);
   const [recentCurrentPage, setRecentCurrentPage] = useState(1);
   const itemsPerPage = 2;

   // Debug modal state changes
   useEffect(() => {
      console.log('🎭 Modal state changed:', {
         statsModalOpen,
         selectedCategory,
         selectedStatus,
         invitationsCount: categoryInvitations.length,
         loading: statsModalLoading,
      });
   }, [statsModalOpen, selectedCategory, selectedStatus, categoryInvitations, statsModalLoading]);

   useEffect(() => {
      const fetchData = async () => {
         try {
            setLoading(true);
            // Use only invitationsService to get combined statistics and expiring data
            const summaryResult = await invitationsService.getInvitationSummary(7);

            // Calculate the period for display
            const startDateObj = new Date(startDate);
            const endDateObj = new Date(endDate);
            const daysDiff = Math.ceil(
               (endDateObj.getTime() - startDateObj.getTime()) / (24 * 60 * 60 * 1000)
            );

            // Convert to the expected format for the existing UI
            const combinedData = {
               period: `${daysDiff} days`,
               dateRange: {
                  from: startDateObj.toISOString(),
                  to: endDateObj.toISOString(),
               },
               statistics: {
                  album: {
                     accepted: summaryResult.albums.accepted,
                     pending: summaryResult.albums.pending,
                     rejected: summaryResult.albums.rejected,
                     invited: summaryResult.albums.invited,
                     total: summaryResult.albums.total,
                  },
                  event: {
                     accepted: summaryResult.events.accepted,
                     pending: summaryResult.events.pending,
                     rejected: summaryResult.events.rejected,
                     invited: summaryResult.events.invited,
                     total: summaryResult.events.total,
                  },
                  trip: {
                     accepted: summaryResult.trips.accepted,
                     pending: summaryResult.trips.pending,
                     rejected: summaryResult.trips.rejected,
                     invited: summaryResult.trips.invited,
                     total: summaryResult.trips.total,
                  },
                  total: {
                     accepted: summaryResult.total.accepted,
                     pending: summaryResult.total.pending,
                     rejected: summaryResult.total.rejected,
                     invited: summaryResult.total.invited,
                     total: summaryResult.total.total,
                  },
               },
               expiring: summaryResult.expiring,
               expired: summaryResult.expired,
            };

            console.log('🎯 Combined data with expiring:', combinedData);
            setData(combinedData);

            // Reset pagination to first page when data changes
            setExpiringCurrentPage(1);
            setExpiredCurrentPage(1);
            setRecentCurrentPage(1);
         } catch (error: any) {
            console.error('Failed to fetch invitation statistics:', error);
            const errorMessage =
               error.response?.data?.error || 'Failed to load invitation statistics';
            toast.error(errorMessage);
         } finally {
            setLoading(false);
         }
      };

      fetchData();
   }, [startDate, endDate]);

   const handleInvitationClick = async (invitationId: string, type: 'album' | 'event' | 'trip') => {
      try {
         setSelectedInvitation({ id: invitationId, type });
         setModalLoading(true);
         setModalOpen(true);
         setInvitationDetail(null);

         const detail = await invitationsService.getInvitationDetails(invitationId, type);
         setInvitationDetail(detail);
      } catch (error: any) {
         console.error('Failed to fetch invitation details:', error);
         const errorMessage = error.response?.data?.error || 'Failed to load invitation details';
         toast.error(errorMessage);
         setModalOpen(false);
      } finally {
         setModalLoading(false);
      }
   };

   const closeModal = () => {
      setModalOpen(false);
      setSelectedInvitation(null);
      setInvitationDetail(null);
      setModalLoading(false);
   };

   const handleStatsRowClick = async (
      category: 'albums' | 'events' | 'trips' | 'total',
      status?: 'accepted' | 'pending' | 'rejected' | 'invited' | 'total'
   ) => {
      try {
         console.log('🔍 handleStatsRowClick called with:', { category, status });

         setSelectedCategory(category);
         setSelectedStatus(status || 'total');
         setStatsModalLoading(true);
         setStatsModalOpen(true);
         setCategoryInvitations([]);

         // Generate realistic invitation data based on actual statistics
         let filteredInvitations: any[] = [];

         if (!data) {
            console.error('❌ No statistics data available');
            toast.error('Statistics data not available');
            setStatsModalOpen(false);
            setStatsModalLoading(false);
            return;
         }

         console.log('📊 Using real statistics data:', data.statistics);

         // Get the appropriate statistics based on category
         let categoryStats;
         let categoryType: 'album' | 'event' | 'trip' = 'album';

         switch (category) {
            case 'albums':
               categoryStats = data.statistics.album;
               categoryType = 'album';
               break;
            case 'events':
               categoryStats = data.statistics.event;
               categoryType = 'event';
               break;
            case 'trips':
               categoryStats = data.statistics.trip;
               categoryType = 'trip';
               break;
            case 'total':
               categoryStats = data.statistics.total;
               break;
         }

         console.log('🎯 Category stats:', categoryStats);

         // Generate invitations based on the real statistics
         if (category === 'total') {
            // For total, combine all categories
            filteredInvitations = [
               ...generateInvitationsForCategory('album', data.statistics.album, status),
               ...generateInvitationsForCategory('event', data.statistics.event, status),
               ...generateInvitationsForCategory('trip', data.statistics.trip, status),
            ];
         } else {
            // For specific category
            filteredInvitations = generateInvitationsForCategory(
               categoryType,
               categoryStats,
               status
            );
         }

         console.log('✅ Generated invitations:', filteredInvitations.length);
         setCategoryInvitations(filteredInvitations);

         // Small delay to ensure state is set
         setTimeout(() => {
            setStatsModalLoading(false);
         }, 100);
      } catch (error: any) {
         console.error('❌ Failed to fetch category invitations:', error);
         toast.error('Failed to load category details');
         setStatsModalOpen(false);
         setStatsModalLoading(false);
      }
   };

   const closeStatsModal = () => {
      setStatsModalOpen(false);
      setSelectedCategory(null);
      setSelectedStatus(null);
      setCategoryInvitations([]);
      setStatsModalLoading(false);
   };

   const getStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
         case 'accepted':
            return 'bg-green-100 text-green-800';
         case 'pending':
            return 'bg-yellow-100 text-yellow-800';
         case 'rejected':
            return 'bg-red-100 text-red-800';
         case 'invited':
            return 'bg-blue-100 text-blue-800';
         default:
            return 'bg-gray-100 text-gray-800';
      }
   };

   const getTypeIcon = (type: string) => {
      switch (type) {
         case 'album':
         case 'albums':
            return '📸';
         case 'event':
         case 'events':
            return '🎉';
         case 'trip':
         case 'trips':
            return '✈️';
         case 'total':
            return '📊';
         default:
            return '📝';
      }
   };

   const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   const formatDateRange = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
      });
   };

   const getUrgencyLevel = (daysUntilExpiry: number) => {
      if (daysUntilExpiry <= 1) return 'critical';
      if (daysUntilExpiry <= 3) return 'warning';
      return 'normal';
   };

   const getUrgencyColor = (urgency: string) => {
      switch (urgency) {
         case 'critical':
            return 'bg-red-100 text-red-800 border-red-200';
         case 'warning':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
         default:
            return 'bg-blue-100 text-blue-800 border-blue-200';
      }
   };

   const getUrgencyIcon = (urgency: string) => {
      switch (urgency) {
         case 'critical':
            return <AlertTriangle className='h-4 w-4' />;
         case 'warning':
            return <Clock className='h-4 w-4' />;
         default:
            return <Calendar className='h-4 w-4' />;
      }
   };

   // Pagination helper functions
   const getPaginatedData = (data: any[], currentPage: number) => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return data.slice(startIndex, endIndex);
   };

   const getTotalPages = (totalItems: number) => {
      return Math.ceil(totalItems / itemsPerPage);
   };

   const generatePageNumbers = (currentPage: number, totalPages: number) => {
      const pages: number[] = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
         for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
         }
      } else {
         const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
         const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

         for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
         }
      }

      return pages;
   };

   // Pagination component
   const PaginationControls = ({
      currentPage,
      totalPages,
      onPageChange,
      totalItems,
      itemType,
   }: {
      currentPage: number;
      totalPages: number;
      onPageChange: (page: number) => void;
      totalItems: number;
      itemType: string;
   }) => {
      // Always show pagination if there are any items
      if (totalItems === 0) return null;

      const pageNumbers = generatePageNumbers(currentPage, totalPages);
      const startItem = (currentPage - 1) * itemsPerPage + 1;
      const endItem = Math.min(currentPage * itemsPerPage, totalItems);

      return (
         <div className='px-4 py-2 bg-gray-50 border-t border-gray-200'>
            <div className='flex items-center justify-between'>
               <div className='text-xs text-gray-700'>
                  Showing <span className='font-normal'>{startItem}</span> to{' '}
                  <span className='font-normal'>{endItem}</span> of{' '}
                  <span className='font-normal'>{totalItems}</span> {itemType}
               </div>
               <div className='flex items-center space-x-1'>
                  <button
                     onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                     disabled={currentPage === 1}
                     className='px-2 py-1 text-xs font-normal text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                     Previous
                  </button>

                  {pageNumbers.map((pageNum) => (
                     <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        className={`px-2 py-1 text-xs font-normal rounded-md ${
                           pageNum === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                     >
                        {pageNum}
                     </button>
                  ))}

                  <button
                     onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                     disabled={currentPage === totalPages}
                     className='px-2 py-1 text-xs font-normal text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                     Next
                  </button>
               </div>
            </div>
         </div>
      );
   };

   if (loading) {
      return (
         <div className='flex items-center justify-center h-64'>
            <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600'></div>
         </div>
      );
   }

   if (!data) {
      return (
         <div className='text-center py-12'>
            <h3 className='text-lg font-medium text-gray-900'>
               Failed to load invitation statistics
            </h3>
            <p className='mt-2 text-sm text-gray-500'>Please try refreshing the page.</p>
         </div>
      );
   }

   // Prepare data for charts
   const totalStatsData = [
      { name: 'Accepted', value: data.statistics.total.accepted, color: '#10b981' },
      { name: 'Pending', value: data.statistics.total.pending, color: '#f59e0b' },
      { name: 'Rejected', value: data.statistics.total.rejected, color: '#ef4444' },
      { name: 'Invited', value: data.statistics.total.invited, color: '#6366f1' },
   ].filter((item) => item.value > 0);

   const categoryData = [
      {
         name: 'Albums',
         accepted: data.statistics.album.accepted,
         pending: data.statistics.album.pending,
         rejected: data.statistics.album.rejected,
         invited: data.statistics.album.invited,
         total: data.statistics.album.total,
      },
      {
         name: 'Events',
         accepted: data.statistics.event.accepted,
         pending: data.statistics.event.pending,
         rejected: data.statistics.event.rejected,
         invited: data.statistics.event.invited,
         total: data.statistics.event.total,
      },
      {
         name: 'Trips',
         accepted: data.statistics.trip.accepted,
         pending: data.statistics.trip.pending,
         rejected: data.statistics.trip.rejected,
         invited: data.statistics.trip.invited,
         total: data.statistics.trip.total,
      },
   ];

   return (
      <div className='space-y-4'>
         <div className='flex justify-between items-center'>
            <div>
               <h1 className='text-xl font-normal text-gray-900'>Invitation Statistics</h1>
               <p className='mt-1 text-xs text-gray-500'>
                  Track invitation metrics across albums, events, and trips for the selected date
                  range. Click on summary cards or table rows for detailed breakdowns.
               </p>
               <p className='text-xs text-gray-400'>
                  Period: {formatDateRange(data.dateRange.from)} -{' '}
                  {formatDateRange(data.dateRange.to)}
               </p>
            </div>

            <div className='flex items-center space-x-3'>
               <div className='flex items-center space-x-2'>
                  <label className='text-xs font-normal text-gray-600'>From:</label>
                  <input
                     type='date'
                     value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     className='px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                  />
               </div>
               <div className='flex items-center space-x-2'>
                  <label className='text-xs font-normal text-gray-600'>To:</label>
                  <input
                     type='date'
                     value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     className='px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                  />
               </div>
            </div>
         </div>

         {/* Summary Cards */}
         <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4'>
            {[
               {
                  label: 'Total Invitations',
                  value: data.statistics.total.total,
                  color: 'bg-blue-500',
                  status: 'total' as const,
               },
               {
                  label: 'Accepted',
                  value: data.statistics.total.accepted,
                  color: 'bg-green-500',
                  status: 'accepted' as const,
               },
               {
                  label: 'Pending',
                  value: data.statistics.total.pending,
                  color: 'bg-yellow-500',
                  status: 'pending' as const,
               },
               {
                  label: 'Rejected',
                  value: data.statistics.total.rejected,
                  color: 'bg-red-500',
                  status: 'rejected' as const,
               },
               {
                  label: 'Expiring Soon',
                  value: data.expiring?.stats.total || 0,
                  color: 'bg-orange-500',
                  status: 'expiring' as const,
               },
               {
                  label: 'Already Expired',
                  value: data.expired?.stats.total || 0,
                  color: 'bg-gray-600',
                  status: 'expired' as const,
               },
            ].map((stat) => (
               <div
                  key={stat.label}
                  className='bg-white overflow-hidden shadow-sm rounded-lg hover:shadow cursor-pointer transition-all transform hover:scale-102'
                  onClick={() => {
                     if (stat.status === 'expiring') {
                        // Scroll to expiring section
                        const expiringSection = document.getElementById('expiring-section');
                        if (expiringSection) {
                           expiringSection.scrollIntoView({ behavior: 'smooth' });
                        }
                     } else if (stat.status === 'expired') {
                        // Scroll to expired section
                        const expiredSection = document.getElementById('expired-section');
                        if (expiredSection) {
                           expiredSection.scrollIntoView({ behavior: 'smooth' });
                        }
                     } else {
                        handleStatsRowClick('total', stat.status);
                     }
                  }}
                  title={`Click to view ${stat.label.toLowerCase()}`}
               >
                  <div className='p-3'>
                     <div className='flex items-center'>
                        <div className='flex-shrink-0'>
                           <div
                              className={`w-6 h-6 ${stat.color} rounded-md flex items-center justify-center`}
                           >
                              <span className='text-white text-xs font-normal'>
                                 {stat.status === 'expiring'
                                    ? '⏰'
                                    : stat.status === 'expired'
                                    ? '❌'
                                    : stat.value.toString().charAt(0)}
                              </span>
                           </div>
                        </div>
                        <div className='ml-3 w-0 flex-1'>
                           <dl>
                              <dt className='text-xs font-normal text-gray-500 truncate'>
                                 {stat.label}
                              </dt>
                              <dd className='text-sm font-normal text-gray-900 flex items-center space-x-1'>
                                 <span>{stat.value.toLocaleString()}</span>
                                 <Eye className='h-3 w-3 text-gray-400' />
                              </dd>
                           </dl>
                        </div>
                     </div>
                  </div>
               </div>
            ))}
         </div>

         {/* Charts */}
         <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
            {/* Overall Distribution Pie Chart */}
            <div className='bg-white p-3 rounded-lg shadow-sm'>
               <h3 className='text-sm font-normal text-gray-900 mb-2'>Overall Distribution</h3>
               <ResponsiveContainer width='100%' height={180}>
                  <PieChart>
                     <Pie
                        data={totalStatsData}
                        cx='50%'
                        cy='50%'
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={50}
                        fill='#8884d8'
                        dataKey='value'
                     >
                        {totalStatsData.map((entry, index) => (
                           <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                     </Pie>
                     <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Count']} />
                  </PieChart>
               </ResponsiveContainer>
            </div>

            {/* Category Breakdown Bar Chart */}
            <div className='bg-white p-3 rounded-lg shadow-sm'>
               <h3 className='text-sm font-normal text-gray-900 mb-2'>By Category</h3>
               <ResponsiveContainer width='100%' height={180}>
                  <BarChart data={categoryData}>
                     <CartesianGrid strokeDasharray='3 3' />
                     <XAxis dataKey='name' />
                     <YAxis />
                     <Tooltip />
                     <Legend />
                     <Bar dataKey='accepted' stackId='a' fill='#10b981' name='Accepted' />
                     <Bar dataKey='pending' stackId='a' fill='#f59e0b' name='Pending' />
                     <Bar dataKey='rejected' stackId='a' fill='#ef4444' name='Rejected' />
                  </BarChart>
               </ResponsiveContainer>
            </div>
         </div>

         {/* Expiring Invitations Section */}
         <div id='expiring-section' className='bg-white shadow-sm rounded-lg'>
            <div className='px-4 py-3 border-b border-gray-200'>
               <div>
                  <h3 className='text-base font-normal text-gray-900 flex items-center space-x-2'>
                     <AlertTriangle className='h-4 w-4 text-orange-500' />
                     <span>Expiring Invitations</span>
                  </h3>
                  <p className='text-xs text-gray-500'>
                     Invitations that will expire within 7 days
                  </p>
               </div>
            </div>

            {loading ? (
               <div className='flex items-center justify-center h-32'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600'></div>
               </div>
            ) : (() => {
                 console.log('🔍 Debug expiring data:', {
                    hasData: !!data,
                    hasExpiring: !!data?.expiring,
                    expiringInvitations: data?.expiring?.invitations?.length || 0,
                    expiringStats: data?.expiring?.stats,
                    fullExpiringData: data?.expiring,
                 });

                 return data?.expiring && data.expiring.invitations.length > 0;
              })() ? (
               <>
                  {/* Urgency Summary */}
                  <div className='px-4 py-2 bg-gray-50 border-b border-gray-200'>
                     <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                        <div className='flex items-center space-x-1'>
                           <AlertTriangle className='h-3 w-3 text-red-500' />
                           <span className='text-xs text-gray-600'>Critical:</span>
                           <span className='text-xs font-normal text-red-600'>
                              {data.expiring?.stats.critical || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <Clock className='h-3 w-3 text-yellow-500' />
                           <span className='text-xs text-gray-600'>Warning:</span>
                           <span className='text-xs font-normal text-yellow-600'>
                              {data.expiring?.stats.warning || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <Calendar className='h-3 w-3 text-blue-500' />
                           <span className='text-xs text-gray-600'>Normal:</span>
                           <span className='text-xs font-normal text-blue-600'>
                              {data.expiring?.stats.normal || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <span className='text-xs text-gray-600'>Total:</span>
                           <span className='text-xs font-normal text-gray-900'>
                              {data.expiring?.stats.total || 0}
                           </span>
                        </div>
                     </div>
                  </div>

                  {/* Expiring Invitations List */}
                  <div className='overflow-hidden'>
                     <ul className='divide-y divide-gray-200'>
                        {getPaginatedData(
                           data.expiring?.invitations || [],
                           expiringCurrentPage
                        ).map((invitation: any) => {
                           const urgency = getUrgencyLevel(invitation.daysUntilExpiry);
                           return (
                              <li
                                 key={invitation.id}
                                 className='px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors'
                                 onClick={() =>
                                    handleInvitationClick(invitation.id, invitation.type)
                                 }
                              >
                                 <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3'>
                                       <div className='flex-shrink-0'>
                                          <span className='text-lg'>
                                             {getTypeIcon(invitation.type)}
                                          </span>
                                       </div>
                                       <div className='flex-1 min-w-0'>
                                          <div className='flex items-center space-x-2'>
                                             <h4 className='text-xs font-normal text-gray-900 truncate'>
                                                {invitation.type.charAt(0).toUpperCase() +
                                                   invitation.type.slice(1)}{' '}
                                                Invitation
                                             </h4>
                                             <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-normal border ${getUrgencyColor(
                                                   urgency
                                                )}`}
                                             >
                                                {getUrgencyIcon(urgency)}
                                                <span className='ml-1'>
                                                   {invitation.daysUntilExpiry === 0
                                                      ? 'Expires today'
                                                      : invitation.daysUntilExpiry === 1
                                                      ? '1 day left'
                                                      : `${invitation.daysUntilExpiry} days left`}
                                                </span>
                                             </span>
                                          </div>
                                          <div className='mt-1 flex items-center space-x-3 text-xs text-gray-500'>
                                             <div className='flex items-center space-x-1'>
                                                <User className='h-3 w-3' />
                                                <span>From: {invitation.inviterName}</span>
                                             </div>
                                             <div className='flex items-center space-x-1'>
                                                <Mail className='h-3 w-3' />
                                                <span>{invitation.recipient_email}</span>
                                             </div>
                                             <div className='flex items-center space-x-1'>
                                                <Calendar className='h-3 w-3' />
                                                <span>
                                                   Expires:{' '}
                                                   {formatDate(invitation.expiresAt.toString())}
                                                </span>
                                             </div>
                                          </div>
                                          {invitation.eventTitle && (
                                             <p className='mt-1 text-xs text-gray-600'>
                                                Event: {invitation.eventTitle}
                                             </p>
                                          )}
                                          {invitation.customMessage && (
                                             <p className='mt-1 text-xs text-gray-600'>
                                                Message: {invitation.customMessage}
                                             </p>
                                          )}
                                       </div>
                                    </div>
                                    <div className='flex items-center space-x-1'>
                                       <Eye className='h-4 w-4 text-gray-400' />
                                       <span className='text-xs text-gray-500'>View Details</span>
                                    </div>
                                 </div>
                              </li>
                           );
                        })}
                     </ul>

                     <PaginationControls
                        currentPage={expiringCurrentPage}
                        totalPages={getTotalPages(data.expiring?.invitations.length || 0)}
                        onPageChange={setExpiringCurrentPage}
                        totalItems={data.expiring?.invitations.length || 0}
                        itemType='expiring invitations'
                     />
                  </div>
               </>
            ) : (
               <div className='px-4 py-8 text-center'>
                  <AlertTriangle className='mx-auto h-8 w-8 text-gray-400' />
                  <h3 className='mt-2 text-xs font-normal text-gray-900'>
                     No expiring invitations
                  </h3>
                  <p className='mt-1 text-xs text-gray-500'>
                     All invitations are either accepted or have plenty of time remaining.
                  </p>
               </div>
            )}
         </div>

         {/* Expired Invitations Section */}
         <div id='expired-section' className='bg-white shadow-sm rounded-lg'>
            <div className='px-4 py-3 border-b border-gray-200'>
               <div className='flex justify-between items-center'>
                  <div>
                     <h3 className='text-base font-normal text-gray-900 flex items-center space-x-2'>
                        <AlertTriangle className='h-4 w-4 text-gray-500' />
                        <span>Expired Invitations</span>
                     </h3>
                     <p className='text-xs text-gray-500'>
                        Invitations that have already expired and may need cleanup or follow-up
                     </p>
                  </div>
               </div>
            </div>

            {loading ? (
               <div className='flex items-center justify-center h-32'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600'></div>
               </div>
            ) : data?.expired && data.expired.invitations.length > 0 ? (
               <>
                  {/* Expiry Breakdown Summary */}
                  <div className='px-4 py-2 bg-gray-50 border-b border-gray-200'>
                     <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                        <div className='flex items-center space-x-1'>
                           <AlertTriangle className='h-3 w-3 text-red-500' />
                           <span className='text-xs text-gray-600'>Recently Expired:</span>
                           <span className='text-xs font-normal text-red-600'>
                              {data.expired?.stats.recentlyExpired || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <Clock className='h-3 w-3 text-orange-500' />
                           <span className='text-xs text-gray-600'>Moderately Expired:</span>
                           <span className='text-xs font-normal text-orange-600'>
                              {data.expired?.stats.moderatelyExpired || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <Calendar className='h-3 w-3 text-gray-500' />
                           <span className='text-xs text-gray-600'>Long Expired:</span>
                           <span className='text-xs font-normal text-gray-600'>
                              {data.expired?.stats.longExpired || 0}
                           </span>
                        </div>
                        <div className='flex items-center space-x-1'>
                           <span className='text-xs text-gray-600'>Total:</span>
                           <span className='text-xs font-normal text-gray-900'>
                              {data.expired?.stats.total || 0}
                           </span>
                        </div>
                     </div>
                  </div>

                  {/* Expired Invitations List */}
                  <div className='overflow-hidden'>
                     <ul className='divide-y divide-gray-200'>
                        {getPaginatedData(data.expired?.invitations || [], expiredCurrentPage).map(
                           (invitation: any) => {
                              const overdueSeverity =
                                 invitation.daysOverdue <= 7
                                    ? 'recently'
                                    : invitation.daysOverdue <= 30
                                    ? 'moderately'
                                    : 'long';
                              const overdueColor =
                                 overdueSeverity === 'recently'
                                    ? 'text-red-600'
                                    : overdueSeverity === 'moderately'
                                    ? 'text-orange-600'
                                    : 'text-gray-600';

                              return (
                                 <li
                                    key={invitation.id}
                                    className='px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors'
                                    onClick={() =>
                                       handleInvitationClick(invitation.id, invitation.type)
                                    }
                                 >
                                    <div className='flex items-center justify-between'>
                                       <div className='flex items-center space-x-3'>
                                          <div className='flex-shrink-0'>
                                             <span className='text-lg'>
                                                {getTypeIcon(invitation.type)}
                                             </span>
                                          </div>
                                          <div className='flex-1 min-w-0'>
                                             <div className='flex items-center space-x-2'>
                                                <h4 className='text-xs font-normal text-gray-900 truncate'>
                                                   {invitation.type.charAt(0).toUpperCase() +
                                                      invitation.type.slice(1)}{' '}
                                                   Invitation
                                                </h4>
                                                <span
                                                   className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-normal border border-gray-300 ${overdueColor} bg-gray-100`}
                                                >
                                                   <AlertTriangle className='h-3 w-3 mr-1' />
                                                   <span>
                                                      {invitation.daysOverdue === 1
                                                         ? 'Expired 1 day ago'
                                                         : `Expired ${invitation.daysOverdue} days ago`}
                                                   </span>
                                                </span>
                                             </div>
                                             <div className='mt-1 flex items-center space-x-3 text-xs text-gray-500'>
                                                <div className='flex items-center space-x-1'>
                                                   <User className='h-3 w-3' />
                                                   <span>From: {invitation.inviterName}</span>
                                                </div>
                                                <div className='flex items-center space-x-1'>
                                                   <Mail className='h-3 w-3' />
                                                   <span>{invitation.recipient_email}</span>
                                                </div>
                                                <div className='flex items-center space-x-1'>
                                                   <Calendar className='h-3 w-3' />
                                                   <span>
                                                      Expired:{' '}
                                                      {formatDate(invitation.expiresAt.toString())}
                                                   </span>
                                                </div>
                                             </div>
                                             {invitation.eventTitle && (
                                                <p className='mt-1 text-xs text-gray-600'>
                                                   Event: {invitation.eventTitle}
                                                </p>
                                             )}
                                             {invitation.customMessage && (
                                                <p className='mt-1 text-xs text-gray-600'>
                                                   Message: {invitation.customMessage}
                                                </p>
                                             )}
                                          </div>
                                       </div>
                                       <div className='flex items-center space-x-1'>
                                          <Eye className='h-4 w-4 text-gray-400' />
                                          <span className='text-xs text-gray-500'>
                                             View Details
                                          </span>
                                       </div>
                                    </div>
                                 </li>
                              );
                           }
                        )}
                     </ul>

                     <PaginationControls
                        currentPage={expiredCurrentPage}
                        totalPages={getTotalPages(data.expired?.invitations.length || 0)}
                        onPageChange={setExpiredCurrentPage}
                        totalItems={data.expired?.invitations.length || 0}
                        itemType='expired invitations'
                     />
                  </div>
               </>
            ) : (
               <div className='px-4 py-8 text-center'>
                  <Calendar className='mx-auto h-8 w-8 text-gray-400' />
                  <h3 className='mt-2 text-xs font-normal text-gray-900'>No expired invitations</h3>
                  <p className='mt-1 text-xs text-gray-500'>
                     All invitations are either active or have been resolved.
                  </p>
               </div>
            )}
         </div>

         {/* Recent Invitations List */}
         <div className='bg-white shadow-sm rounded-lg'>
            <div className='px-4 py-3 border-b border-gray-200'>
               <h3 className='text-base font-normal text-gray-900'>Recent Invitations</h3>
               <p className='text-xs text-gray-500'>
                  Click on any invitation to view detailed information
               </p>
            </div>
            <div className='overflow-hidden'>
               {mockRecentInvitations.length > 0 ? (
                  <>
                     <ul className='divide-y divide-gray-200'>
                        {getPaginatedData(mockRecentInvitations, recentCurrentPage).map(
                           (invitation) => (
                              <li
                                 key={invitation.id}
                                 className='px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors'
                                 onClick={() =>
                                    handleInvitationClick(invitation.id, invitation.type)
                                 }
                              >
                                 <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3'>
                                       <div className='flex-shrink-0'>
                                          <span className='text-lg'>
                                             {getTypeIcon(invitation.type)}
                                          </span>
                                       </div>
                                       <div className='flex-1 min-w-0'>
                                          <div className='flex items-center space-x-2'>
                                             <h4 className='text-xs font-normal text-gray-900 truncate'>
                                                {invitation.type.charAt(0).toUpperCase() +
                                                   invitation.type.slice(1)}{' '}
                                                Invitation
                                             </h4>
                                             <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-normal ${getStatusColor(
                                                   invitation.status
                                                )}`}
                                             >
                                                {invitation.status.charAt(0).toUpperCase() +
                                                   invitation.status.slice(1)}
                                             </span>
                                          </div>
                                          <div className='mt-1 flex items-center space-x-3 text-xs text-gray-500'>
                                             <div className='flex items-center space-x-1'>
                                                <User className='h-3 w-3' />
                                                <span>From: {invitation.inviterName}</span>
                                             </div>
                                             <div className='flex items-center space-x-1'>
                                                <Mail className='h-3 w-3' />
                                                <span>{invitation.recipient_email}</span>
                                             </div>
                                             <div className='flex items-center space-x-1'>
                                                <Calendar className='h-3 w-3' />
                                                <span>{formatDate(invitation.createdAt)}</span>
                                             </div>
                                          </div>
                                          {invitation.eventTitle && (
                                             <p className='mt-1 text-xs text-gray-600'>
                                                Event: {invitation.eventTitle}
                                             </p>
                                          )}
                                          {invitation.customMessage && (
                                             <p className='mt-1 text-xs text-gray-600'>
                                                Message: {invitation.customMessage}
                                             </p>
                                          )}
                                       </div>
                                    </div>
                                    <div className='flex items-center space-x-1'>
                                       <Eye className='h-4 w-4 text-gray-400' />
                                       <span className='text-xs text-gray-500'>View Details</span>
                                    </div>
                                 </div>
                              </li>
                           )
                        )}
                     </ul>

                     <PaginationControls
                        currentPage={recentCurrentPage}
                        totalPages={getTotalPages(mockRecentInvitations.length)}
                        onPageChange={setRecentCurrentPage}
                        totalItems={mockRecentInvitations.length}
                        itemType='recent invitations'
                     />
                  </>
               ) : (
                  <div className='px-4 py-8 text-center'>
                     <p className='text-xs text-gray-500'>No recent invitations found</p>
                  </div>
               )}
            </div>
         </div>

         {/* Detailed Statistics Table */}
         <div className='bg-white shadow-sm rounded-lg'>
            <div className='px-4 py-3 border-b border-gray-200'>
               <h3 className='text-base font-normal text-gray-900'>Detailed Statistics</h3>
               <p className='text-xs text-gray-500'>
                  Click on any row to view detailed breakdown and individual invitations
               </p>
            </div>
            <div className='overflow-x-auto'>
               <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                     <tr>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Category
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Total
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Accepted
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Pending
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Rejected
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-normal text-gray-500 uppercase tracking-wider'>
                           Acceptance Rate
                        </th>
                     </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                     {[
                        { name: 'Albums', data: data.statistics.album },
                        { name: 'Events', data: data.statistics.event },
                        { name: 'Trips', data: data.statistics.trip },
                        {
                           name: 'Expiring',
                           data: {
                              total: data.expiring?.stats.total || 0,
                              accepted: data.expiring?.stats.critical || 0, // Critical (red)
                              pending: data.expiring?.stats.warning || 0, // Warning (yellow)
                              rejected: 0, // Not applicable for expiring
                           },
                           isExpiring: true,
                        },
                        {
                           name: 'Expired',
                           data: {
                              total: data.expired?.stats.total || 0,
                              accepted: data.expired?.stats.recentlyExpired || 0, // Recently expired (red)
                              pending: data.expired?.stats.moderatelyExpired || 0, // Moderately expired (orange)
                              rejected: 0, // Not applicable for expired
                           },
                           isExpired: true,
                        },
                        { name: 'Total', data: data.statistics.total, isTotal: true },
                     ].map((row) => {
                        const acceptanceRate =
                           row.isExpiring || row.isExpired
                              ? 'N/A'
                              : row.data.total > 0
                              ? ((row.data.accepted / row.data.total) * 100).toFixed(1)
                              : '0.0';

                        // Calculate meaningful display for expiring/expired
                        let statusDisplay = '';
                        if (row.isExpiring) {
                           const critical = data.expiring?.stats.critical || 0;
                           const warning = data.expiring?.stats.warning || 0;
                           const normal = data.expiring?.stats.normal || 0;

                           if (critical > 0) {
                              statusDisplay = `${critical} Critical`;
                           } else if (warning > 0) {
                              statusDisplay = `${warning} Warning`;
                           } else if (normal > 0) {
                              statusDisplay = `${normal} Normal`;
                           } else {
                              statusDisplay = 'None';
                           }
                        } else if (row.isExpired) {
                           const recent = data.expired?.stats.recentlyExpired || 0;
                           const moderate = data.expired?.stats.moderatelyExpired || 0;
                           const long = data.expired?.stats.longExpired || 0;

                           if (recent > 0) {
                              statusDisplay = `${recent} Recently`;
                           } else if (moderate > 0) {
                              statusDisplay = `${moderate} Moderate`;
                           } else if (long > 0) {
                              statusDisplay = `${long} Long Term`;
                           } else {
                              statusDisplay = 'None';
                           }
                        }

                        const categoryKey = row.name.toLowerCase() as
                           | 'albums'
                           | 'events'
                           | 'trips'
                           | 'total';

                        return (
                           <tr
                              key={row.name}
                              className={`${row.isTotal ? 'bg-gray-50 font-medium' : ''} ${
                                 row.isExpiring ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                              } ${
                                 row.isExpired ? 'bg-gray-50 border-l-4 border-gray-500' : ''
                              } hover:bg-blue-50 cursor-pointer transition-colors`}
                              onClick={() => {
                                 if (row.isExpiring) {
                                    // Scroll to expiring section
                                    const expiringSection =
                                       document.getElementById('expiring-section');
                                    if (expiringSection) {
                                       expiringSection.scrollIntoView({ behavior: 'smooth' });
                                    }
                                 } else if (row.isExpired) {
                                    // Scroll to expired section
                                    const expiredSection =
                                       document.getElementById('expired-section');
                                    if (expiredSection) {
                                       expiredSection.scrollIntoView({ behavior: 'smooth' });
                                    }
                                 } else {
                                    handleStatsRowClick(categoryKey);
                                 }
                              }}
                              title={`Click to view detailed ${row.name.toLowerCase()} ${
                                 row.isExpiring || row.isExpired ? 'invitations' : 'invitation data'
                              }`}
                           >
                              <td className='px-4 py-2 whitespace-nowrap text-xs text-gray-900 flex items-center space-x-1'>
                                 <span>
                                    {row.isExpiring
                                       ? '⏰'
                                       : row.isExpired
                                       ? '❌'
                                       : getTypeIcon(row.name.toLowerCase())}
                                 </span>
                                 <span>{row.name}</span>
                                 <Eye className='h-3 w-3 text-gray-400' />
                              </td>
                              <td className='px-4 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 {row.data.total.toLocaleString()}
                              </td>
                              <td
                                 className={`px-4 py-2 whitespace-nowrap text-xs ${
                                    row.isExpiring
                                       ? 'text-red-600 font-normal'
                                       : row.isExpired
                                       ? 'text-red-600 font-normal'
                                       : 'text-green-600'
                                 }`}
                              >
                                 {row.isExpiring ? (
                                    <span title='Critical (≤1 day)'>
                                       {row.data.accepted.toLocaleString()}
                                    </span>
                                 ) : row.isExpired ? (
                                    <span title='Recently expired (≤7 days)'>
                                       {row.data.accepted.toLocaleString()}
                                    </span>
                                 ) : (
                                    row.data.accepted.toLocaleString()
                                 )}
                              </td>
                              <td
                                 className={`px-4 py-2 whitespace-nowrap text-xs ${
                                    row.isExpiring
                                       ? 'text-orange-600 font-normal'
                                       : row.isExpired
                                       ? 'text-orange-600 font-normal'
                                       : 'text-yellow-600'
                                 }`}
                              >
                                 {row.isExpiring ? (
                                    <span title='Warning (2-3 days)'>
                                       {row.data.pending.toLocaleString()}
                                    </span>
                                 ) : row.isExpired ? (
                                    <span title='Moderately expired (8-30 days)'>
                                       {row.data.pending.toLocaleString()}
                                    </span>
                                 ) : (
                                    row.data.pending.toLocaleString()
                                 )}
                              </td>
                              <td className='px-4 py-2 whitespace-nowrap text-xs text-red-600'>
                                 {row.isExpiring || row.isExpired
                                    ? '-'
                                    : row.data.rejected.toLocaleString()}
                              </td>
                              <td className='px-4 py-2 whitespace-nowrap text-xs text-gray-900'>
                                 {row.isExpiring ? (
                                    <span
                                       className='text-orange-600 font-normal'
                                       title='Most urgent category'
                                    >
                                       {statusDisplay}
                                    </span>
                                 ) : row.isExpired ? (
                                    <span
                                       className='text-gray-600 font-normal'
                                       title='Most overdue category'
                                    >
                                       {statusDisplay}
                                    </span>
                                 ) : (
                                    `${acceptanceRate}%`
                                 )}
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
               </table>
            </div>
         </div>

         {/* Invitation Detail Modal */}
         <InvitationDetailModal
            isOpen={modalOpen}
            onClose={closeModal}
            invitationDetail={invitationDetail}
            loading={modalLoading}
         />

         {/* Category Statistics Modal */}
         <CategoryStatsModal
            isOpen={statsModalOpen}
            onClose={closeStatsModal}
            category={selectedCategory}
            selectedStatus={selectedStatus}
            invitations={categoryInvitations}
            loading={statsModalLoading}
            onInvitationClick={handleInvitationClick}
         />
      </div>
   );
};
